import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { toast } from 'sonner';
import { ExportApiErrorPayload } from './export.dto';

// Matches the lifetime of the signed download URL, so the manual fallback button
// stays on screen for exactly as long as it still works.
const DOWNLOAD_TOAST_DURATION_MS = 300_000;

export const openDownloadUrl = (url: string): void => {
  if (typeof window !== 'undefined') {
    const toast_id = toast.success(t('inbox:export.download_started'), {
      duration: DOWNLOAD_TOAST_DURATION_MS,
      action: {
        label: t('inbox:export.download'),
        onClick: () => {
          window.open(url, '_blank', 'noopener,noreferrer');
          toast.dismiss(toast_id);
        },
      },
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

export class ConversationExportJobError extends Error {
  constructor(public readonly errorCode: string | null) {
    super(errorCode ?? 'generation_failed');
  }
}

// The server did not confirm the filter it was asked for, so the file may be wider than requested.
export const EXPORT_FILTER_NOT_APPLIED = 'filter_not_applied';

// A response that cannot prove the filter was applied must not become a download.
export const assertExportFilterApplied = (
  requested: string | undefined,
  applied: string | null | undefined,
): void => {
  if (requested && applied !== requested) {
    throw new ConversationExportJobError(EXPORT_FILTER_NOT_APPLIED);
  }
};

export const handleExportFailure = (error: unknown, context: string) => {
  if (error instanceof ConversationExportJobError) {
    const message =
      error.errorCode === 'too_large'
        ? t('inbox:export.errors.tooLarge')
        : error.errorCode === 'xlsx_too_large'
          ? t('inbox:export.errors.xlsxTooLarge')
          : error.errorCode === 'xlsx_too_wide'
            ? t('inbox:export.errors.xlsxTooWide')
            : error.errorCode === 'no_data'
              ? t('inbox:export.errors.noConversations')
              : error.errorCode === 'timed_out'
                ? t('inbox:export.errors.timedOut')
                : error.errorCode === EXPORT_FILTER_NOT_APPLIED
                  ? t('inbox:export.errors.filterNotApplied')
                  : t('inbox:export.errors.failed');
    toast.error(message, { duration: 15_000 });
    console.error(context, error);
    return;
  }

  const respData: ExportApiErrorPayload = isAxiosError<ExportApiErrorPayload>(error)
    ? (error.response?.data ?? {})
    : {};

  const rootError = respData?.errors?.root?.[0];
  const rootCode = rootError?.code;
  const reason = rootError?.options?.reason;

  const message =
    reason === 'conversation_export_too_large'
      ? t('inbox:export.errors.tooLarge')
      : reason === 'data_gathering_export_xlsx_too_large'
        ? t('inbox:export.errors.xlsxTooLarge')
        : reason === 'conversation_export_active'
          ? t('inbox:export.errors.alreadyRunning')
          : reason === 'data_gathering_export_active'
            ? t('inbox:export.errors.alreadyRunningGatheredData')
            : reason === 'conversation_export_metadata_filter_empty'
              ? t('inbox:export.errors.noMetadataMatches')
              : rootCode === 'BAD_REQUEST'
                ? t('inbox:export.errors.noConversations')
                : t('inbox:export.errors.generic');

  toast.error(message, {
    duration: 15_000,
  });
  console.error(context, error);
};
